import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  AlertTriangle,
  Ship,
  FileText,
  ChevronRight,
  ShieldAlert,
  CircleCheck,
  Layers,
} from "lucide-react";
import { Eyebrow, SectionShell } from "@/components/site/Primitives";
import { SCENARIO } from "@/lib/scenario";

const STATES = [
  { key: "detect", tab: "Portfolio — Detect", short: "Detect" },
  { key: "decide", tab: "Decision — Decide", short: "Decide" },
  { key: "verify", tab: "Evidence — Verify", short: "Verify" },
];

function Tile({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <p className="text-[11px] leading-tight text-natsu-warm-white/50">{label}</p>
      <p className={`mt-1.5 font-display text-base ${accent ? "text-natsu-gold" : "text-natsu-warm-white"}`}>{value}</p>
    </div>
  );
}

function Detect() {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.25fr_1fr]" data-testid="product-state-detect">
      <div className="rounded-2xl border border-natsu-amber/25 bg-natsu-amber/[0.05] p-5">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-natsu-amber">
            <AlertTriangle className="h-3.5 w-3.5" /> Priority Event
          </span>
          <span className="rounded-full bg-natsu-amber/15 px-2.5 py-1 text-[11px] font-medium text-natsu-amber">Requires Attention</span>
        </div>
        <h4 className="mt-3 font-display text-xl text-natsu-warm-white">{SCENARIO.eventShort}</h4>
        <p className="mt-2 text-[13px] leading-relaxed text-natsu-warm-white/60">{SCENARIO.situation}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-white/[0.05] px-2.5 py-1.5 text-[12px] text-natsu-warm-white/75"><Ship className="h-3.5 w-3.5 text-natsu-blue-soft" /> 2 vessels</span>
          <span className="inline-flex items-center gap-1.5 rounded-md bg-white/[0.05] px-2.5 py-1.5 text-[12px] text-natsu-warm-white/75"><FileText className="h-3.5 w-3.5 text-natsu-blue-soft" /> 3 contracts</span>
          <span className="inline-flex items-center gap-1.5 rounded-md bg-white/[0.05] px-2.5 py-1.5 text-[12px] text-natsu-warm-white/80">Counterparty context linked</span>
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <Tile label="Estimated Exposure Range" value={SCENARIO.exposure} accent />
        <Tile label="Recommended Action Window" value={SCENARIO.window} />
        <button className="flex items-center justify-between rounded-xl border border-natsu-gold/30 bg-natsu-gold/[0.06] px-4 py-3 text-left transition-colors hover:bg-natsu-gold/[0.12]">
          <span className="text-[12.5px] font-semibold text-natsu-gold">Open Decision Workspace</span>
          <ChevronRight className="h-4 w-4 text-natsu-gold" />
        </button>
      </div>
    </div>
  );
}

function Decide() {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1.1fr]" data-testid="product-state-decide">
      <div className="flex flex-col gap-4">
        <div className="rounded-2xl border border-natsu-gold/25 bg-natsu-gold/[0.06] p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-natsu-gold/80">Recommended action</p>
          <p className="mt-1.5 font-display text-xl text-natsu-warm-white">{SCENARIO.action}</p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Tile label="Exposure Range" value={SCENARIO.exposure} accent />
            <Tile label="Action Window" value={SCENARIO.window} />
            <Tile label="Confidence" value={SCENARIO.confidence} />
            <Tile label="Scenarios" value={SCENARIO.scenariosEvaluated} />
          </div>
        </div>
        <div className="flex items-start gap-2.5 rounded-xl border border-natsu-purple/20 bg-natsu-purple/[0.06] p-4">
          <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-natsu-purple" />
          <div>
            <p className="text-[12.5px] font-semibold text-natsu-warm-white/85">Human Review Required</p>
            <p className="text-[12px] text-natsu-warm-white/55">Authorised signatory approval before action.</p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-natsu-warm-white/50">Scenario comparison</p>
        <ul className="mt-3 space-y-2">
          {SCENARIO.options.map((o, i) => {
            const recommended = i === 1;
            return (
              <li
                key={o}
                className={`flex items-center justify-between rounded-xl border px-4 py-3 ${recommended ? "border-natsu-gold/35 bg-natsu-gold/[0.08]" : "border-white/8 bg-white/[0.02]"}`}
              >
                <span className={`text-[13px] ${recommended ? "font-semibold text-natsu-warm-white" : "text-natsu-warm-white/70"}`}>
                  {i + 1}. {o}
                </span>
                {recommended && (
                  <span className="rounded-full bg-natsu-gold px-2 py-0.5 text-[10.5px] font-semibold text-natsu-navy">Recommended</span>
                )}
              </li>
            );
          })}
        </ul>
        <p className="mt-4 text-[11.5px] text-natsu-warm-white/45">
          Critical assumptions apply. Ranges and qualitative confidence are used in place of false precision.
        </p>
      </div>
    </div>
  );
}

function Verify() {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.1fr_1fr]" data-testid="product-state-verify">
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-natsu-warm-white/50">Commercial rationale</p>
        <p className="mt-2 text-[13px] leading-relaxed text-natsu-warm-white/70">
          The recommendation weighs charter-party provisions and route disruption
          against modelled bunker-cost movement and counterparty history to protect
          commercial margin within the decision window.
        </p>
        <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.14em] text-natsu-warm-white/50">Linked source categories</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {SCENARIO.sourceCategories.map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5 rounded-md bg-natsu-blue/10 px-2.5 py-1 text-[11.5px] text-natsu-blue-soft ring-1 ring-natsu-blue/15">
              <Layers className="h-3 w-3" /> {c}
            </span>
          ))}
        </div>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-natsu-warm-white/50">Key assumptions</p>
            <ul className="mt-2 space-y-1 text-[12px] text-natsu-warm-white/65">
              <li>· Charter-party provisions as interpreted</li>
              <li>· Bunker-cost movement within modelled range</li>
            </ul>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-natsu-warm-white/50">Limitations</p>
            <ul className="mt-2 space-y-1 text-[12px] text-natsu-warm-white/65">
              <li>· Subject to counterparty response</li>
              <li>· Estimates use illustrative sample data</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="rounded-2xl border border-natsu-purple/20 bg-natsu-purple/[0.05] p-5">
          <div className="flex items-center justify-between">
            <span className="text-[12.5px] font-semibold text-natsu-warm-white/85">Governance &amp; approval status</span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-natsu-purple/15 px-2.5 py-1 text-[11px] font-medium text-natsu-purple ring-1 ring-natsu-purple/25">
              <ShieldAlert className="h-3 w-3" /> Human Review Required
            </span>
          </div>
          <ul className="mt-4 space-y-2 text-[12.5px] text-natsu-warm-white/70">
            <li className="flex items-center gap-2"><CircleCheck className="h-3.5 w-3.5 text-natsu-green" /> Evidence status: linked &amp; traceable</li>
            <li className="flex items-center gap-2"><CircleCheck className="h-3.5 w-3.5 text-natsu-green" /> Assumptions &amp; limitations documented</li>
            <li className="flex items-center gap-2"><CircleCheck className="h-3.5 w-3.5 text-natsu-green" /> Source traceability: full audit trail</li>
            <li className="flex items-center gap-2"><ShieldAlert className="h-3.5 w-3.5 text-natsu-purple" /> Approval status: pending authorised approval</li>
          </ul>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-natsu-warm-white/50">Recommendation change triggers</p>
          <ul className="mt-3 space-y-1.5 text-[12.5px] text-natsu-warm-white/65">
            <li>· Material change in bunker-cost movement</li>
            <li>· Updated charter-party interpretation</li>
            <li>· New counterparty or route information</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

const RENDER = { detect: Detect, decide: Decide, verify: Verify };

export function ProductPreview() {
  const [active, setActive] = useState("detect");
  const reduce = useReducedMotion();
  const activeLabel = STATES.find((s) => s.key === active)?.tab;
  const Body = RENDER[active];

  return (
    <SectionShell id="product" tone="white" prominent>
      <div className="on-dark grain relative overflow-hidden rounded-3xl bg-natsu-navy p-6 sm:p-10 lg:p-14">
        <div className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full bg-natsu-blue/10 blur-[130px]" />

        <div className="relative z-[1] max-w-3xl">
          <Eyebrow tone="dark" color="gold">NATSU Commercial Intelligence Platform</Eyebrow>
          <h2 className="display mt-6 text-4xl text-natsu-warm-white sm:text-5xl">Detect. Decide. Verify.</h2>
          <p className="mt-5 measure text-[15px] leading-relaxed text-natsu-warm-white/70 sm:text-lg">
            One governed workflow connects commercial events, quantified exposure,
            scenario analysis, executive recommendations, evidence and human approval.
          </p>
        </div>

        {/* Desktop tabs */}
        <div className="relative z-[1] mt-9 hidden gap-2 sm:flex">
          {STATES.map((s) => (
            <button
              key={s.key}
              onClick={() => setActive(s.key)}
              data-testid={`product-tab-${s.key}`}
              className={`rounded-full px-5 py-2.5 text-[13px] font-semibold tracking-tight transition-all duration-300 ${active === s.key ? "bg-natsu-gold text-natsu-navy" : "border border-white/20 text-natsu-warm-white/80 hover:text-natsu-warm-white"}`}
            >
              {s.tab}
            </button>
          ))}
        </div>

        {/* Mobile segmented control */}
        <div className="relative z-[1] mt-8 sm:hidden">
          <div className="grid grid-cols-3 gap-1 rounded-full border border-white/12 bg-white/[0.03] p-1">
            {STATES.map((s) => (
              <button
                key={s.key}
                onClick={() => setActive(s.key)}
                data-testid={`product-segment-${s.key}`}
                className={`rounded-full py-2 text-[13px] font-semibold transition-all duration-300 ${active === s.key ? "bg-natsu-gold text-natsu-navy" : "text-natsu-warm-white/80"}`}
              >
                {s.short}
              </button>
            ))}
          </div>
          <p className="mt-3 text-[13px] font-medium text-natsu-warm-white/60">{activeLabel}</p>
        </div>

        {/* Product shell */}
        <div className="relative z-[1] mt-5 overflow-hidden rounded-2xl border border-white/10 bg-natsu-navy-deep/60">
          <div className="flex items-center justify-between border-b border-white/[0.07] px-4 py-3 sm:px-5">
            <div className="flex items-center gap-2.5">
              <img src="/natsu-mark.png" alt="" className="h-5 w-5" aria-hidden="true" />
              <span className="text-[12.5px] font-medium text-natsu-warm-white/70">NATSU Commercial Intelligence Platform</span>
            </div>
            <span className="rounded-full bg-white/[0.06] px-2.5 py-1 text-[10.5px] font-medium tracking-wide text-natsu-warm-white/55">Illustrative</span>
          </div>

          <div className="p-4 sm:p-6">
            {reduce ? (
              <Body />
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Body />
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
